import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CompareContext = createContext();

const MAX_COMPARE_ITEMS = 2;
const STORAGE_KEY = 'conflux_compare_products';

export const CompareProvider = ({ children }) => {
  const [compareItems, setCompareItems] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(compareItems));
    } catch { /* ignore */ }
  }, [compareItems]);

  const addToCompare = useCallback((product) => {
    setCompareItems(prev => {
      if (prev.find(i => i.id === product.id)) return prev;
      if (prev.length >= MAX_COMPARE_ITEMS) {
        alert(`You can compare up to ${MAX_COMPARE_ITEMS} products at a time. Remove one first.`);
        return prev;
      }
      return [...prev, { ...product, addedAt: Date.now() }];
    });
  }, []);

  const removeFromCompare = useCallback((id) => {
    setCompareItems(prev => prev.filter(i => i.id !== id));
  }, []);

  const isInCompare = useCallback((id) => {
    return compareItems.some(i => i.id === id);
  }, [compareItems]);

  const clearCompare = useCallback(() => {
    setCompareItems([]);
  }, []);

  const compareCount = compareItems.length;
  const isFull = compareItems.length >= MAX_COMPARE_ITEMS;

  return (
    <CompareContext.Provider value={{
      compareItems,
      addToCompare,
      removeFromCompare,
      isInCompare,
      clearCompare,
      compareCount,
      isFull,
      MAX_COMPARE_ITEMS
    }}>
      {children}
    </CompareContext.Provider>
  );
};

export const useCompare = () => {
  const context = useContext(CompareContext);
  if (!context) {
    throw new Error('useCompare must be used within a CompareProvider');
  }
  return context;
};
